import { useEffect, useRef, useState } from 'react'
import type { Quote } from '@shared/types'
import { api } from '../../lib/api'
import { useStore } from '../../store'
import { dirClass, fmtPct, fmtPrice } from '../../lib/format'
import { cn, pollWhileVisible } from '../../lib/utils'
import { displaySymbol, normalizeSymbol } from '../../lib/symbol'
import { portfolioTodayPL } from '../../lib/portfolio'
import { SESSION_LABEL, useExtendedQuotes, useLiveQuotes, useMarketSession } from '../../hooks/useMarketData'

export function TickerTape() {
  const settings = useStore((s) => s.settings)
  const positions = useStore((s) => s.positions)
  const transactions = useStore((s) => s.transactions)
  const watchlist = useStore((s) => s.watchlist)
  const session = useMarketSession()
  const tickers = settings?.tickerSymbols ?? []
  const showValue = settings?.showPortfolioInTicker ?? false
  const showHoldings = settings?.showHoldingsInTicker ?? false
  const showWatchlist = settings?.showWatchlistInTicker ?? false
  const holdingSyms = positions.map((p) => p.symbol)
  const loadPortfolio = useStore((s) => s.loadPortfolio)
  // Holdings can change at the broker (a worker fills, a trade in the broker's app): keep the tape's list current.
  useEffect(() => {
    if (!showValue && !showHoldings) return
    return pollWhileVisible(() => loadPortfolio(), 120_000)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showValue, showHoldings])

  const allSymbols = [
    ...new Set([
      ...tickers,
      ...(showValue || showHoldings ? holdingSyms : []),
      ...(showWatchlist ? watchlist : []),
    ]),
  ]
  const key = allSymbols.join(',')
  const [quotes, setQuotes] = useState<Record<string, Quote>>({})
  const extended = useExtendedQuotes(allSymbols, session)
  const live = useLiveQuotes(allSymbols)

  const viewportRef = useRef<HTMLDivElement>(null)
  const firstRef = useRef<HTMLDivElement>(null)
  const [animate, setAnimate] = useState(false)
  const [duration, setDuration] = useState(40)

  useEffect(() => {
    if (!allSymbols.length) {
      setQuotes({})
      return
    }
    let alive = true
    const load = () =>
      api
        .quotes(allSymbols)
        .then((qs) => {
          if (!alive) return
          const m: Record<string, Quote> = {}
          for (const q of qs) m[q.symbol] = q
          setQuotes(m)
        })
        .catch(() => {})
    // The server shares one cached quote per symbol with every tab; a hidden tab doesn't ask at all.
    const stop = pollWhileVisible(load, 60_000)
    return () => {
      alive = false
      stop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const eff = (sym: string) => {
    const full = normalizeSymbol(sym)
    const q = quotes[full]
    const e = extended[full]
    const lv = live[full]
    // real-time WS first, then extended snapshot, then delayed REST
    const price = lv?.price ?? e?.price ?? q?.price
    if (price == null) return null
    // Outside regular hours, baseline against the extended quote's prevClose (the regular
    // close) so the % is the pre/after-hours move, not the move since yesterday's close
    // (which double-counts the regular day). `e` only exists in pre/after/closed.
    const prevClose = e?.prevClose ?? q?.previousClose ?? price
    const change = price - prevClose
    return {
      price,
      change,
      prevClose,
      changePct: prevClose > 0 ? (change / prevClose) * 100 : 0,
      ext: session !== 'regular',
    }
  }

  const displaySyms = [...new Set([...tickers, ...(showHoldings ? holdingSyms : []), ...(showWatchlist ? watchlist : [])])]
  const entries = displaySyms.map((sym) => ({ sym, e: eff(sym) })).filter((x) => x.e)

  // Ticker shows the portfolio's TODAY P&L (broker-correct: positions opened
  // today count vs their buy price, older ones vs yesterday's close).
  let portValue = 0
  if (showValue) {
    for (const p of positions) {
      const e = eff(p.symbol)
      if (e) portValue += p.quantity * e.price
    }
  }
  const hasPort = showValue && portValue > 0
  const todayPL = hasPort
    ? portfolioTodayPL(
        positions,
        transactions,
        (s) => eff(s)?.price ?? null,
        (s) => eff(s)?.prevClose ?? null,
      )
    : { gain: 0, pct: 0 }
  const extActive = session !== 'regular'

  // Marquee: scroll left in an infinite loop when the content overflows.
  useEffect(() => {
    const measure = () => {
      const vp = viewportRef.current
      const f = firstRef.current
      if (!vp || !f) return
      const w = f.scrollWidth
      const over = w > vp.clientWidth + 8
      setAnimate(over)
      if (over) setDuration(Math.max(18, Math.round(w / 40)))
    }
    measure()
    const ro = new ResizeObserver(measure)
    if (viewportRef.current) ro.observe(viewportRef.current)
    if (firstRef.current) ro.observe(firstRef.current)
    return () => ro.disconnect()
  }, [entries.length, hasPort, extActive, key])

  if (!entries.length && !hasPort) return <div className="min-w-0 flex-1" />

  const items = (
    <>
      {hasPort && (
        <div className="flex shrink-0 items-center gap-1.5 text-[12px]">
          <span className="font-semibold text-fg">PORT</span>
          <span className="num text-fg">{fmtPrice(portValue, '$')}</span>
          <span className={cn('num', dirClass(todayPL.gain))}>{fmtPct(todayPL.pct)}</span>
        </div>
      )}
      {entries.map(
        ({ sym, e }) =>
          e && (
            <div key={sym} className="flex shrink-0 items-center gap-1.5 text-[12px]">
              <span className="font-medium text-fg-muted">{displaySymbol(sym)}</span>
              <span className="num text-fg">{fmtPrice(e.price)}</span>
              <span className={cn('num', dirClass(e.changePct))}>{fmtPct(e.changePct)}</span>
              {e.ext && (
                <span className="rounded bg-[var(--color-inset)] px-1 text-[9px] font-semibold uppercase text-fg-subtle">
                  {session === 'pre' ? 'PRE' : 'AH'}
                </span>
              )}
            </div>
          ),
      )}
    </>
  )

  return (
    <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
      {extActive && (
        <span className="shrink-0 rounded bg-[var(--color-surface-3)] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-fg-subtle">
          {SESSION_LABEL[session]}
        </span>
      )}
      <div ref={viewportRef} className="relative min-w-0 flex-1 overflow-hidden">
        <div
          className={cn('flex w-max items-center', animate && 'ticker-anim')}
          style={animate ? { animationDuration: `${duration}s` } : undefined}
        >
          <div ref={firstRef} className="flex items-center gap-4 pr-4">
            {items}
          </div>
          {animate && (
            <div className="flex items-center gap-4 pr-4" aria-hidden>
              {items}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
